import { Trash2, TrendingDown, TrendingUp, Euro, Leaf, Home, Flame, Droplet, Wind, DoorClosed, Layers, Banknote, Info } from 'lucide-react';
import { Button } from '../ui/Button';
import { deleteImprovement } from '../../services/work-scenarios-service';
import type { ScenarioImprovement, ImprovementType } from '../../types/work-scenarios';

interface ImprovementsListProps {
  improvements: ScenarioImprovement[];
  onDelete: () => void;
}

const IMPROVEMENT_ICONS: Record<ImprovementType, any> = {
  windows: DoorClosed,
  wall_insulation: Layers,
  roof_insulation: Home,
  floor_insulation: Layers,
  heating: Flame,
  hot_water: Droplet,
  ventilation: Wind,
  heating_control: Flame,
};

const IMPROVEMENT_LABELS: Record<ImprovementType, string> = {
  windows: 'Fenêtres',
  wall_insulation: 'Isolation des murs',
  roof_insulation: 'Isolation de la toiture',
  floor_insulation: 'Isolation du plancher',
  heating: 'Système de chauffage',
  hot_water: 'Eau chaude sanitaire',
  ventilation: 'Ventilation',
  heating_control: 'Régulation du chauffage',
};

export function ImprovementsList({ improvements, onDelete }: ImprovementsListProps) {
  async function handleDelete(improvementId: string) {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette amélioration ?')) {
      return;
    }

    try {
      await deleteImprovement(improvementId);
      onDelete();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete improvement');
    }
  }

  if (improvements.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        Aucun travaux planifié. Cliquez sur "Ajouter des travaux" pour commencer.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {improvements.map((improvement) => (
        <div
          key={improvement.id}
          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4 flex-1">
              <div className="p-3 bg-blue-50 rounded-lg">
                {(() => {
                  const Icon = IMPROVEMENT_ICONS[improvement.improvement_type];
                  return <Icon className="w-6 h-6 text-blue-600" />;
                })()}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-gray-900">
                    {IMPROVEMENT_LABELS[improvement.improvement_type]}
                    {improvement.improvement_type === 'wall_insulation' && improvement.energy_savings_breakdown?.insulation_type && (
                      <span className={`ml-2 text-xs px-2 py-1 rounded font-semibold ${
                        improvement.energy_savings_breakdown.insulation_type === 'ITE'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {improvement.energy_savings_breakdown.insulation_type}
                      </span>
                    )}
                  </h3>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">Avant:</span>
                    <span className="text-gray-900">{improvement.before_description}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">Après:</span>
                    <span className="font-medium text-blue-600">
                      {improvement.after_description}
                    </span>
                  </div>
                  {improvement.improvement_type === 'wall_insulation' &&
                   improvement.insulation_coverage_percentage &&
                   improvement.insulation_coverage_percentage < 100 && (
                    <div className="flex items-center gap-2 text-sm mt-2">
                      <span className="text-gray-600">Surface isolée:</span>
                      <span className="font-medium text-orange-600">
                        {improvement.insulation_coverage_percentage}% des murs ({Math.round(improvement.affected_surface || 0)} m²)
                      </span>
                    </div>
                  )}
                  {improvement.insulation_coverage_notes && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>Note:</span>
                      <span className="italic">{improvement.insulation_coverage_notes}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    {(improvement.energy_savings || 0) >= 0 ? (
                      <TrendingDown className="w-4 h-4 text-blue-600" />
                    ) : (
                      <TrendingUp className="w-4 h-4 text-red-600" />
                    )}
                    <div>
                      <div className={`font-semibold ${
                        (improvement.energy_savings || 0) >= 0
                          ? 'text-gray-900'
                          : 'text-red-700'
                      }`}>
                        {(improvement.energy_savings || 0) >= 0
                          ? `${improvement.energy_savings?.toLocaleString() || 0} kWh/an`
                          : `+${Math.abs(improvement.energy_savings || 0).toLocaleString()} kWh/an`}
                      </div>
                      <div className="text-xs text-gray-500">
                        {(improvement.energy_savings || 0) >= 0
                          ? 'Économies d\'énergie'
                          : 'Augmentation'}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Euro className="w-4 h-4 text-green-600" />
                    <div>
                      <div className="font-semibold text-gray-900">
                        {improvement.cost_savings?.toLocaleString() || 0} €/an
                      </div>
                      <div className="text-xs text-gray-500">Économies financières</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Banknote className="w-4 h-4 text-orange-600" />
                    <div>
                      <div className="font-semibold text-gray-900">
                        {improvement.estimated_cost && improvement.estimated_cost > 0
                          ? `${improvement.estimated_cost.toLocaleString()} €`
                          : '—'}
                      </div>
                      <div className="text-xs text-gray-500">Coût estimé</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Leaf className={`w-4 h-4 ${
                      (improvement.ges_reduction || 0) >= 0
                        ? 'text-emerald-600'
                        : 'text-red-600'
                    }`} />
                    <div>
                      <div className={`font-semibold ${
                        (improvement.ges_reduction || 0) >= 0
                          ? 'text-gray-900'
                          : 'text-red-700'
                      }`}>
                        {(improvement.ges_reduction || 0) >= 0
                          ? `${improvement.ges_reduction?.toLocaleString() || 0} kgCO2/an`
                          : `+${Math.abs(improvement.ges_reduction || 0).toLocaleString()} kgCO2/an`}
                      </div>
                      <div className="text-xs text-gray-500">
                        {(improvement.ges_reduction || 0) >= 0
                          ? 'Réduction GES'
                          : 'Augmentation GES'}
                      </div>
                    </div>
                  </div>
                </div>

                {(improvement as any).is_gas_to_electric_replacement && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <div className="font-semibold text-amber-900 mb-1">
                          Compromis énergétique
                        </div>
                        <p className="text-amber-800">
                          Ce remplacement <strong className="text-red-700">augmente la consommation d'énergie</strong> mais <strong className="text-green-700">réduit les émissions GES</strong>.
                          Associé à de l'isolation, l'impact global reste positif.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {improvement.estimated_cost && improvement.estimated_cost > 0 && improvement.cost_savings && improvement.cost_savings > 0 && (
                  <div className="mt-3">
                    {(() => {
                      const roiYears = improvement.estimated_cost / improvement.cost_savings;
                      const bgColor = roiYears < 8 ? 'bg-green-100 text-green-800' : roiYears <= 15 ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800';
                      return (
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${bgColor}`}>
                          Retour sur investissement: {roiYears.toFixed(1)} ans
                        </div>
                      );
                    })()}
                  </div>
                )}

                {improvement.energy_savings_breakdown && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="text-sm font-semibold text-blue-900 mb-2">
                      Détail des économies d'énergie
                      {improvement.energy_savings_breakdown.insulation_type && (
                        <span className="ml-2 text-xs font-normal bg-blue-200 px-2 py-1 rounded">
                          {improvement.energy_savings_breakdown.insulation_type}
                        </span>
                      )}
                    </div>
                    <div className="space-y-1 text-xs">
                      {improvement.energy_savings_breakdown.insulation_savings !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-gray-700">Isolation des murs:</span>
                          <span className="font-semibold text-gray-900">
                            {improvement.energy_savings_breakdown.insulation_savings.toLocaleString()} kWh/an
                          </span>
                        </div>
                      )}
                      {improvement.energy_savings_breakdown.thermal_bridges_reduction !== undefined &&
                        improvement.energy_savings_breakdown.thermal_bridges_reduction > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-700">
                            Réduction ponts thermiques
                            {improvement.energy_savings_breakdown.insulation_type === 'ITE' ? ' (75%)' : ' (25%)'}:
                          </span>
                          <span className="font-semibold text-blue-700">
                            +{improvement.energy_savings_breakdown.thermal_bridges_reduction.toLocaleString()} kWh/an
                          </span>
                        </div>
                      )}
                      {improvement.energy_savings_breakdown.total !== undefined && (
                        <div className="flex justify-between pt-1 border-t border-blue-200 mt-1">
                          <span className="font-semibold text-gray-900">Total:</span>
                          <span className="font-bold text-blue-900">
                            {improvement.energy_savings_breakdown.total.toLocaleString()} kWh/an
                          </span>
                        </div>
                      )}
                    </div>
                    {improvement.energy_savings_breakdown.insulation_type === 'ITE' &&
                      improvement.energy_savings_breakdown.thermal_bridges_reduction &&
                      improvement.energy_savings_breakdown.thermal_bridges_reduction > 0 && (
                      <div className="mt-2 text-xs text-blue-800">
                        L'ITE permet de traiter efficacement les ponts thermiques structurels (liaisons murs-planchers, menuiseries, refends)
                      </div>
                    )}
                  </div>
                )}

                {improvement.affected_surface && (
                  <div className="mt-3 pt-3 border-t border-gray-100 text-sm">
                    <span className="text-gray-600">Surface concernée:</span>
                    <span className="ml-2 font-semibold text-gray-900">
                      {improvement.affected_surface.toFixed(1)} m²
                    </span>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => handleDelete(improvement.id)}
              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
